/**
 * PipelineSettingsPanel — pipeline-level settings (step-through controls, variables CRUD).
 * Extracted from PipelineStepSettings to reduce component size.
 *
 * Store-connected: reads from useEditorStore.
 */

import React from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useEditorStore } from '@/store/useEditorStore';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PipelineSettingsPanel: React.FC = () => {
  const pipelineVariables = useEditorStore((s) => s.pipelineVariables);
  const setPipelineVariable = useEditorStore((s) => s.setPipelineVariable);
  const removePipelineVariable = useEditorStore((s) => s.removePipelineVariable);

  const [newVarKey, setNewVarKey] = React.useState('');
  const [newVarValue, setNewVarValue] = React.useState('');
  const [newVarError, setNewVarError] = React.useState<string | null>(null);

  return (
    <div className="space-y-4">
      {/* Variables */}
      <div>
        <div className="text-lg font-semibold">Variables</div>
        <div className="text-sm text-muted-foreground mt-1">
          Use {'{{var}}'} in the input and step settings.
        </div>
      </div>

      <div className="space-y-2">
        {Object.keys(pipelineVariables).length === 0 ? (
          <div className="text-sm text-muted-foreground">No variables defined.</div>
        ) : (
          Object.entries(pipelineVariables)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                <div className="w-48 min-w-0">
                  <div className="text-xs font-medium text-muted-foreground truncate" title={key}>{key}</div>
                </div>
                <Input value={value} onChange={(e) => setPipelineVariable(key, e.target.value)} placeholder="Value" />
                <Button variant="ghost" size="sm" onClick={() => removePipelineVariable(key)} aria-label={`Remove ${key}`}>
                  ✕
                </Button>
              </div>
            ))
        )}
      </div>

      {/* Add variable */}
      <div className="border-t border-border pt-4 space-y-2">
        <div className="text-sm font-semibold">Add variable</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input value={newVarKey}
            onChange={(e) => { setNewVarKey(e.target.value); setNewVarError(null); }}
            placeholder="Key (e.g. apiKey)" />
          <Input value={newVarValue}
            onChange={(e) => { setNewVarValue(e.target.value); setNewVarError(null); }}
            placeholder="Value" />
        </div>
        {newVarError && <div className="text-sm text-destructive">{newVarError}</div>}
        <div>
          <Button variant="secondary" size="sm" onClick={() => {
            const key = newVarKey.trim();
            if (!key) { setNewVarError('Key is required.'); return; }
            setPipelineVariable(key, newVarValue);
            setNewVarKey('');
            setNewVarValue('');
            setNewVarError(null);
          }}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};
